/**
 * OEM Deals Engine — cart upsell ("complete the fit") element (OpenEye Marketing).
 *
 * Self-managing so it survives the cart drawer's morph on every add:
 *  - the element is marked data-skip-subtree-update in Liquid, so morph never
 *    wipes the cards it injects;
 *  - after a successful add it removes that card and tops the list back up to
 *    the limit (fetching fresh recommendations, excluding what's in the cart),
 *    so the shopper can keep adding without the list vanishing.
 */
import { addVariant } from '@theme/oem-cart-add';

class OEMCartUpsell extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    if (this.hasAttribute('data-auto')) this.#topUp();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get #row() {
    return this.querySelector('.oem-upsell__row');
  }

  get #limit() {
    return parseInt(this.dataset.limit || '4', 10);
  }

  get #root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  #cardCount() {
    return this.querySelectorAll('.oem-upsell__card').length;
  }

  #shownHandles() {
    return new Set(
      [...this.querySelectorAll('.oem-upsell__card')].map((el) => el.dataset.handle).filter(Boolean)
    );
  }

  async #cart() {
    try {
      const res = await fetch(`${this.#root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const ids = new Set((cart.items || []).map((i) => String(i.product_id)));
      const first = cart.items && cart.items[0] ? String(cart.items[0].product_id) : this.dataset.firstProductId;
      return { ids, first };
    } catch (e) {
      return { ids: new Set(), first: this.dataset.firstProductId };
    }
  }

  async #topUp() {
    if (this.#busy) return;
    const row = this.#row;
    if (!row) return;

    this.#busy = true;
    try {
      const { ids: inCart, first } = await this.#cart();

      // Drop any cards for products already in the cart.
      this.querySelectorAll('.oem-upsell__card').forEach((card) => {
        if (card.dataset.productId && inCart.has(String(card.dataset.productId))) card.remove();
      });

      if (this.#cardCount() >= this.#limit || !first) return;

      let products = [];
      try {
        const res = await fetch(
          `${this.#root}recommendations/products.json?product_id=${first}&limit=10&intent=related`
        );
        if (res.ok) products = (await res.json()).products || [];
      } catch (e) {
        return; // recommendations are a bonus — never surface an error
      }

      const shown = this.#shownHandles();
      for (const product of products) {
        if (this.#cardCount() >= this.#limit) break;
        if (!product.available || inCart.has(String(product.id)) || shown.has(product.handle)) continue;
        row.appendChild(this.#card(product));
        shown.add(product.handle);
      }
    } finally {
      this.#busy = false;
    }
  }

  #card(product) {
    const variants = product.variants || [];
    const firstAvailable = variants.find((v) => v.available) || variants[0];

    const card = document.createElement('div');
    card.className = 'oem-upsell__card';
    card.dataset.handle = product.handle;
    card.dataset.productId = String(product.id);

    const imageUrl = product.featured_image
      ? product.featured_image + (product.featured_image.includes('?') ? '&' : '?') + 'width=120'
      : null;
    const img = imageUrl ? `<img src="${imageUrl}" alt="" width="60" height="60" loading="lazy">` : '';

    const select =
      variants.length > 1
        ? `<select class="oem-upsell__select" data-oem-upsell-select aria-label="Choose option">${variants
            .map(
              (v) =>
                `<option value="${v.id}"${v.available ? '' : ' disabled'}${
                  firstAvailable && v.id === firstAvailable.id ? ' selected' : ''
                }>${this.#escape(v.title)}</option>`
            )
            .join('')}</select>`
        : '';
    const action = `${select}<button type="button" class="button oem-upsell__add" data-variant-id="${
      firstAvailable ? firstAvailable.id : ''
    }" data-product-id="${product.id}">Add</button>`;

    card.innerHTML = `${img}<div class="oem-upsell__info"><span class="oem-upsell__title"></span><span class="oem-upsell__price">${this.#formatMoney(
      product.price
    )}</span></div>${action}`;
    card.querySelector('.oem-upsell__title').textContent = product.title;
    return card;
  }

  #onClick = (event) => {
    const button = event.target.closest('button.oem-upsell__add');
    if (!button || !(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    this.#add(button);
  };

  async #add(button) {
    const card = button.closest('.oem-upsell__card');
    const select = card && card.querySelector('[data-oem-upsell-select]');
    const variantId = (select && select.value) || button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.textContent = 'Adding…';
    const ok = await addVariant(variantId, 1, `oem-upsell-${variantId}`);

    if (ok) {
      // Remove the added card and refill so the list stays full — the shopper
      // keeps adding without anything disappearing.
      if (card) card.remove();
      this.#topUp();
    } else {
      button.textContent = "Couldn't add";
      setTimeout(() => {
        button.textContent = 'Add';
        button.disabled = false;
      }, 1200);
    }
  }

  #escape(str) {
    const d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const noDecimals = Math.round(cents / 100).toString();
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/<[^>]*>/g, '');
  }
}

if (!customElements.get('oem-cart-upsell')) {
  customElements.define('oem-cart-upsell', OEMCartUpsell);
}
